import { FormControl, FormHelperText, TextField } from "@mui/material";
import { ChangeEvent, FC, FocusEvent, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  ICustomField,
  ValidationHelpers,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";

interface ITextareaTemplateProps extends ICustomField {
  json?: boolean;
}

export const TextareaTemplate: FC<ITextareaTemplateProps> = ({
  id,
  name,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  json,
  e2eData = "textarea-template",
}) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });

  const { t } = useTranslation();

  const setJson = (e: ChangeEvent<HTMLInputElement>): void => {
    try {
      setValue(name as string, e.target.value);
      JSON.parse(e.target.value);
      clearErrors(name as string);
    } catch (_error) {
      setError(name as string, { message: t("errors.invalidJSON") });
    }
  };

  const onChangeHandler = useEvent(
    (event: FocusEvent<HTMLTextAreaElement>): void => {
      const value = event.target?.value;
      checkCleanupNestedValidation(name, errors);

      setValue(name as string, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const onBlurJson = (): void => {
    const jsonValue = getValues()[name as string];
    setValue(name as string, JSON.parse(JSON.parse(jsonValue)));
  };

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  return name ? (
    <FullWidthTextFieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({
          field: { value, onChange: _onChange, onBlur, ...restField },
          fieldState: { error },
        }) => {
          return (
            <FormControl
              required={requiredComputed}
              data-e2e-template="textarea-template"
              data-e2e-field-name={name}
              error={!!error}
            >
              <TextField
                {...restField}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                label={formLabelTranslated}
                required={requiredComputed}
                error={!!error}
                value={value || ""}
                fullWidth
                multiline
                rows={4}
                onChange={json ? setJson : onChangeHandler}
                onBlur={json ? onBlurJson : onBlur}
                disabled={formAttributes?.disabled}
              />
              {error && (
                <FormHelperText data-e2e="textarea-template-input">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </FullWidthTextFieldWrapper>
  ) : null;
};
